import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

export type ItemId = string | number;

export interface MultiSelectReturn {
  selectedIds: Set<ItemId>;
  toggle: (id: ItemId) => void;
  toggleAll: (allIds: ItemId[]) => void;
  clearSelection: () => void;
  isSelected: (id: ItemId) => boolean;
  isAllSelected: boolean;
  selectedCount: number;
}

/**
 * Hook for managing multi-select state in tables.
 * Auto-clears selection when data changes (e.g. page change, refetch, filter change).
 * 
 * @param dataRef - Array of data items currently displayed in the table.
 *                  Selection is cleared when data content changes (detected via item IDs/keys).
 */
export const useMultiSelect = (dataRef?: unknown[]): MultiSelectReturn => {
  const [selectedIds, setSelectedIds] = useState<Set<ItemId>>(new Set());
  
  // Create a stable signature of the current data for comparison
  // Uses first item's id, last item's id, and length to detect changes
  const dataSignature = useMemo(() => {
    if (!dataRef || dataRef.length === 0) return 'empty';
    const first = (dataRef[0] as { id?: ItemId })?.id ?? 'no-id';
    const last = (dataRef[dataRef.length - 1] as { id?: ItemId })?.id ?? 'no-id';
    return `${dataRef.length}-${first}-${last}`;
  }, [dataRef]);
  
  const prevSignatureRef = useRef(dataSignature);

  // Clear selection when data signature changes
  useEffect(() => {
    if (prevSignatureRef.current !== dataSignature) {
      setSelectedIds(new Set());
      prevSignatureRef.current = dataSignature;
    }
  }, [dataSignature]);

  const toggle = useCallback((id: ItemId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback((allIds: ItemId[]) => {
    setSelectedIds((prev) => {
      const allSelected = allIds.length > 0 && allIds.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(allIds);
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback(
    (id: ItemId) => selectedIds.has(id),
    [selectedIds]
  );

  const isAllSelected = useMemo(() => {
    if (!dataRef || dataRef.length === 0) return false;
    if (selectedIds.size !== dataRef.length) return false;
    // Since selection is cleared when data changes (see useEffect above),
    // we only need to check that sizes match. All selected IDs must be valid.
    return true;
  }, [selectedIds, dataRef]);

  return useMemo(
    () => ({
      selectedIds,
      toggle,
      toggleAll,
      clearSelection,
      isSelected,
      isAllSelected,
      selectedCount: selectedIds.size,
    }),
    [selectedIds, toggle, toggleAll, clearSelection, isSelected, isAllSelected]
  );
};
